import { fromJS } from "immutable";
import i18next from "i18next";
import { quantityGridActions } from "actions/quantityGridActions.js";
import { qtyGridActions } from "actions/StockModal/qtyGridActions.js";
import { buyplanDetailsActions } from "actions/buyplanDetailsActions.js";
import { sizeBreakActions } from "actions/StockModal/sizeBreakActions.js";
import { qtyGridBarcodeScanningActions } from "actions/StockModal/barcodeScanningActions.js";
import { debounceNotification } from "utils/debounceNotification.js";

const initialState = fromJS({
  isLoading: true,
  error: null,
  data: {
    ats: {},
    headers: {},
    size_mins: [],
    attribute_sequence: [],
    brand: {
      is_cumulative_ats: null,
      is_default_ats_zero: null,
      amount_visible_offset: null,
      low_quantity_offset: null
    },
    abilities: {
      can_edit_buyplan: null,
      show_discount_percent: false
    },
    po_dates: {},
    product_release: {
      id: null,
      is_ats_visible: null,
      order_qty_block: null,
      product_requirements: []
    },
    buyplan_item: {
      id: null,
      buyplan_id: null,
      intended_months: [],
      filled_months: [],
      pre_order_intended_months: [],
      pre_order_filled_months: [],
      total_quantity: null,
      total_price: null
    },
    delivery_months: [
      {
        delivery_month_short: null,
        month_date: null,
        index: null,
        attr_rows: {}
      }
    ],
    pre_order_delivery_month:
      {
        delivery_month_short: null,
        month_date: null,
        index: null,
        attr_rows: {}
      },
      is_pre_order_product : null,
      pre_order_message : null,
      pre_order_month_and_date : null,
  },
  scanLogs: [],
  barcodeScanningFailed: false
});

export default (state = initialState, action) => {
  let sizeBreakSize, newQty, sizeBreakKey, isQtyValid, qtyErrMsg, qtyGrid, sizeBreakMeta, updatedState;

  switch (action && action.type) {
    case quantityGridActions.startLoader:
    case qtyGridActions.startLoader:
    case qtyGridBarcodeScanningActions.startLoader:
      return state.set("isLoading", true);
    case quantityGridActions.reset:
    case qtyGridActions.reset:
      return initialState;
    case quantityGridActions.updateSuccess:
      return state.setIn(["data"], fromJS(action.payload.data));
    case quantityGridActions.success:
    case qtyGridActions.qtyGridSuccess:
    case qtyGridBarcodeScanningActions.buyplanQtyGridSuccess:
    case qtyGridBarcodeScanningActions.stockModelQtyGridSuccess:
      return state
        .merge({
          isLoading: false,
          error: null
        })
        .set("data", fromJS(action.payload.data));
    case qtyGridBarcodeScanningActions.buyplanQtyGridFailure:
      return state
        .merge({
          isLoading: false,
          error: action.payload.data
        })
        .set("data", initialState.get("data"))
        .set("barcodeScanningFailed", true);
    case quantityGridActions.failure:
    case qtyGridActions.failure:
    case qtyGridBarcodeScanningActions.stockModelQtyGridFailure:
      return state
        .merge({
          isLoading: false,
          error: action.payload.data
        })
        .set("data", initialState.get("data"));
    case quantityGridActions.updateFailure:
    case qtyGridActions.updateQtyFailure:
      return state.set("error", action.payload.data);
    case qtyGridActions.updateQtySuccess:
      qtyGrid = fromJS(action.payload.data.stock_model_item_data.qty_grid);
      sizeBreakMeta = action.payload.metadata;
      updatedState = state;

      if (
        sizeBreakMeta &&
        sizeBreakMeta.sizeBreakKey !== undefined &&
        sizeBreakMeta.sizeBreakSize !== undefined
      ) {
        // Only update the single size break that was edited
        sizeBreakKey = sizeBreakMeta.sizeBreakKey;
        sizeBreakSize = sizeBreakMeta.sizeBreakSize;

        const updatedSizeBreakData = qtyGrid.getIn([
          "size_breaks",
          sizeBreakKey,
          sizeBreakSize
        ]);

        if (updatedSizeBreakData) {
          updatedState = updatedState.setIn(
            ["data", "size_breaks", sizeBreakKey, sizeBreakSize],
            updatedSizeBreakData
          );
        }

        // Update ATS data if the response includes it
        if (qtyGrid.has("ats")) {
          const atsUpdate = qtyGrid.get("ats");
          if (atsUpdate) {
            const existingAts = updatedState.getIn(["data", "ats"]);
            updatedState = existingAts
              ? updatedState.mergeIn(["data", "ats"], atsUpdate)
              : updatedState.setIn(["data", "ats"], atsUpdate);
          }
        }
      } else {
        // Fallback: merge the full grid (e.g. responses without metadata)
        updatedState = updatedState.mergeIn(["data"], qtyGrid);
      }

      const stockModelItemUpdate = fromJS({
        total_price: action.payload.data.total_price,
        total_price_formatted: action.payload.data.total_price_formatted,
        total_quantity: action.payload.data.total_quantity
      });

      const existingStockModelItem = updatedState.getIn(["data", "stock_model_item"]);
      return existingStockModelItem
        ? updatedState.mergeIn(["data", "stock_model_item"], stockModelItemUpdate)
        : updatedState.setIn(["data", "stock_model_item"], stockModelItemUpdate);
    case buyplanDetailsActions.success:
      return state.mergeIn(
        ["data"],
        fromJS({
          brand: action.payload.data.brand,
          abilities: action.payload.data.abilities
        })
      );
    case sizeBreakActions.locallyUpdateStockQty:
      sizeBreakSize = action.payload.sizeBreakSize;
      newQty = action.payload.newQty;
      sizeBreakKey = action.payload.sizeBreakKey || "";

      const countQty = state.getIn([
        "data",
        "size_breaks",
        sizeBreakKey,
        sizeBreakSize,
        "count_quantity",
        "quantity"
      ]);

      const newFillQuantity = newQty - countQty > 0 ? newQty - countQty : "";

      [isQtyValid, qtyErrMsg] = validateQty(newQty);

      // Show error if there's an invalid value
      if (!isQtyValid && qtyErrMsg) {
        debounceNotification({
          type: "danger",
          message: qtyErrMsg
        });
      }

      return state
        .set("error", isQtyValid ? null : qtyErrMsg)
        .setIn(
          [
            "data",
            "size_breaks",
            sizeBreakKey,
            sizeBreakSize,
            "stock_quantity",
            "quantity"
          ],
          isQtyValid ? newQty : ""
        )
        .setIn(
          [
            "data",
            "size_breaks",
            sizeBreakKey,
            sizeBreakSize,
            "fill_quantity",
            "quantity"
          ],
          isQtyValid ? newFillQuantity : ""
        )
        .updateIn(
          [
            "data",
            "size_breaks",
            sizeBreakKey,
            sizeBreakSize,
            "fill_quantity",
            "_computeKey"
          ],
          (v) => (v || 0) + 1
        );
    case sizeBreakActions.locallyUpdateCountQty:
      sizeBreakSize = action.payload.sizeBreakSize;
      newQty = action.payload.newQty;
      sizeBreakKey = action.payload.sizeBreakKey || "";

      const stockQty = state.getIn([
        "data",
        "size_breaks",
        sizeBreakKey,
        sizeBreakSize,
        "stock_quantity",
        "quantity"
      ]);

      const newFillQty = stockQty - newQty > 0 ? stockQty - newQty : 0;

      [isQtyValid, qtyErrMsg] = validateQty(newQty);

      // Show error if there's an invalid value
      if (!isQtyValid && qtyErrMsg) {
        debounceNotification({
          type: "danger",
          message: qtyErrMsg
        });
      }

      return state
        .set("error", isQtyValid ? null : qtyErrMsg)
        .setIn(
          [
            "data",
            "size_breaks",
            sizeBreakKey,
            sizeBreakSize,
            "count_quantity",
            "quantity"
          ],
          isQtyValid ? newQty : ""
        )
        .setIn(
          [
            "data",
            "size_breaks",
            sizeBreakKey,
            sizeBreakSize,
            "fill_quantity",
            "quantity"
          ],
          isQtyValid ? newFillQty : ""
        )
        .updateIn(
          [
            "data",
            "size_breaks",
            sizeBreakKey,
            sizeBreakSize,
            "fill_quantity",
            "_computeKey"
          ],
          (v) => (v || 0) + 1
        );
    case sizeBreakActions.locallyUpdateFillQty:
      sizeBreakSize = action.payload.sizeBreakSize;
      newQty = action.payload.newQty;
      sizeBreakKey = action.payload.sizeBreakKey || "";

      const countQuantity = state.getIn([
        "data",
        "size_breaks",
        sizeBreakKey,
        sizeBreakSize,
        "count_quantity",
        "quantity"
      ]);

      const newStockQty = newQty + countQuantity;

      [isQtyValid, qtyErrMsg] = validateQty(newQty);

      // Show error if there's an invalid value
      if (!isQtyValid && qtyErrMsg) {
        debounceNotification({
          type: "danger",
          message: qtyErrMsg
        });
      }

      return state
        .set("error", isQtyValid ? null : qtyErrMsg)
        .setIn(
          [
            "data",
            "size_breaks",
            sizeBreakKey,
            sizeBreakSize,
            "stock_quantity",
            "quantity"
          ],
          isQtyValid ? newStockQty : ""
        )
        .setIn(
          [
            "data",
            "size_breaks",
            sizeBreakKey,
            sizeBreakSize,
            "fill_quantity",
            "quantity"
          ],
          isQtyValid ? newQty : ""
        )
        .updateIn(
          [
            "data",
            "size_breaks",
            sizeBreakKey,
            sizeBreakSize,
            "stock_quantity",
            "_computeKey"
          ],
          (v) => (v || 0) + 1
        );
    case qtyGridBarcodeScanningActions.storeScanLogs:
      const updatedLogs = state.get("scanLogs").merge(action.payload.newLog)
      return state.set("scanLogs", updatedLogs);
    case qtyGridBarcodeScanningActions.resetScanLogs:
      return initialState;
    default:
      return state;
  }
};

const validateQty = (newQty) => {
  newQty = parseInt(newQty);
  let nonNumericValueloclisationData = i18next.t("qtyGrid.sizeBreak.inputValidations.nonNumericValue");
  let outOfRangeloclisationData = i18next.t("qtyGrid.sizeBreak.inputValidations.outOfRange");
  if (isNaN(newQty)) {
    return [false, nonNumericValueloclisationData];
  } else if (newQty < 0 || newQty > 99999) {
    return [false, outOfRangeloclisationData];
  } else {
    return [true, null];
  }
};
